import { useState, useRef, useEffect } from 'react';
import { Plane, Calendar, ArrowLeftRight, ArrowRight, X } from 'lucide-react';
import type { Airport, FlightType, CreateFlightInput } from '@/types';
import { useAirportSearch } from '@/hooks';

interface AddFlightFormProps {
  onAddFlight: (input: CreateFlightInput) => Promise<void>;
}

export function AddFlightForm({ onAddFlight }: AddFlightFormProps) {
  const fromSearch = useAirportSearch();
  const toSearch = useAirportSearch();
  const [flightDate, setFlightDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [flightType, setFlightType] = useState<FlightType>('direct');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFromResults, setShowFromResults] = useState(false);
  const [showToResults, setShowToResults] = useState(false);

  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fromRef.current && !fromRef.current.contains(event.target as Node)) {
        setShowFromResults(false);
      }
      if (toRef.current && !toRef.current.contains(event.target as Node)) {
        setShowToResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectAirport = (
    airport: Airport,
    type: 'from' | 'to'
  ) => {
    if (type === 'from') {
      fromSearch.setSelectedAirport(airport);
      fromSearch.setQuery(`${airport.iata} - ${airport.city}`);
      setShowFromResults(false);
    } else {
      toSearch.setSelectedAirport(airport);
      toSearch.setQuery(`${airport.iata} - ${airport.city}`);
      setShowToResults(false);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    type: 'from' | 'to'
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const search = type === 'from' ? fromSearch : toSearch;
      const query = search.query.trim().toUpperCase();
      
      // If query is exactly 3 characters (IATA code), look for exact match
      if (query.length === 3) {
        const exactMatch = search.results.find(
          airport => airport.iata.toUpperCase() === query
        );
        
        if (exactMatch) {
          handleSelectAirport(exactMatch, type);
        }
      } else if (search.results.length > 0) {
        // Otherwise, select the first result
        handleSelectAirport(search.results[0], type);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fromSearch.selectedAirport || !toSearch.selectedAirport) return;

    setIsSubmitting(true);
    try {
      const trimmedNotes = notes.trim();
      await onAddFlight({
        fromAirport: fromSearch.selectedAirport,
        toAirport: toSearch.selectedAirport,
        flightDate,
        flightType,
        ...(trimmedNotes && { notes: trimmedNotes }),
      });

      // Reset form
      fromSearch.clearSearch();
      toSearch.clearSearch();
      setFlightDate(new Date().toISOString().split('T')[0]);
      setFlightType('direct');
      setNotes('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    fromSearch.selectedAirport &&
    toSearch.selectedAirport &&
    flightDate &&
    !isSubmitting;

  return (
    <div className="glass rounded-xl md:rounded-2xl p-4 md:p-6 glow-box">
      <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2">
        <Plane className="w-4 h-4 md:w-5 md:h-5 text-primary-500" />
        Add a Flight
      </h3>

      <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
        {/* From Airport */}
        <div ref={fromRef} className="relative">
          <label className="block text-xs md:text-sm text-white/70 mb-1">From</label>
          <input
            type="text"
            value={fromSearch.query}
            onChange={(e) => {
              fromSearch.setQuery(e.target.value);
              fromSearch.setSelectedAirport(null);
              setShowFromResults(true);
            }}
            onFocus={() => setShowFromResults(true)}
            onKeyDown={(e) => handleKeyDown(e, 'from')}
            placeholder="Search airport or city..."
            className="w-full text-sm md:text-base"
          />
          {fromSearch.selectedAirport && (
            <button
              type="button"
              onClick={() => fromSearch.clearSearch()}
              className="absolute right-3 top-8 text-white/50 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Search results dropdown */}
          {showFromResults && fromSearch.results.length > 0 && (
            <div className="search-results flight-scroll mobile-scroll visible">
              {fromSearch.results.map((airport) => (
                <div
                  key={airport.iata}
                  className="search-result"
                  onClick={() => handleSelectAirport(airport, 'from')}
                >
                  <span className="result-code">{airport.iata}</span>
                  <span className="result-info">
                    <span className="result-name">{airport.city}</span>
                    <span className="result-country">{airport.country}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Flight direction indicator */}
        <div className="flex justify-center">
          <div className="text-2xl text-primary-500">
            {flightType === 'return' ? (
              <ArrowLeftRight className="w-6 h-6" />
            ) : (
              <ArrowRight className="w-6 h-6" />
            )}
          </div>
        </div>

        {/* To Airport */}
        <div ref={toRef} className="relative">
          <label className="block text-sm text-white/70 mb-1">To</label>
          <input
            type="text"
            value={toSearch.query}
            onChange={(e) => {
              toSearch.setQuery(e.target.value);
              toSearch.setSelectedAirport(null);
              setShowToResults(true);
            }}
            onFocus={() => setShowToResults(true)}
            onKeyDown={(e) => handleKeyDown(e, 'to')}
            placeholder="Search airport or city..."
            className="w-full"
          />
          {toSearch.selectedAirport && (
            <button
              type="button"
              onClick={() => toSearch.clearSearch()}
              className="absolute right-3 top-8 text-white/50 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Search results dropdown */}
          {showToResults && toSearch.results.length > 0 && (
            <div className="search-results flight-scroll visible">
              {toSearch.results.map((airport) => (
                <div
                  key={airport.iata}
                  className="search-result"
                  onClick={() => handleSelectAirport(airport, 'to')}
                >
                  <span className="result-code">{airport.iata}</span>
                  <span className="result-info">
                    <span className="result-name">{airport.city}</span>
                    <span className="result-country">{airport.country}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Flight Type Toggle */}
        <div>
          <label className="block text-xs md:text-sm text-white/70 mb-2">Flight Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFlightType('direct')}
              className={`flex-1 py-2 px-3 md:px-4 rounded-xl transition-all flex items-center justify-center gap-2 text-sm md:text-base ${
                flightType === 'direct'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              <ArrowRight className="w-4 h-4" />
              <span className="hidden sm:inline">One Way</span>
              <span className="sm:hidden">One</span>
            </button>
            <button
              type="button"
              onClick={() => setFlightType('return')}
              className={`flex-1 py-2 px-3 md:px-4 rounded-xl transition-all flex items-center justify-center gap-2 text-sm md:text-base ${
                flightType === 'return'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              <ArrowLeftRight className="w-4 h-4" />
              Return
            </button>
          </div>
          <p className="text-xs text-white/50 mt-1">
            {flightType === 'return'
              ? 'Return flights count as 2 flights in your stats'
              : 'One way flight counts as 1 flight'}
          </p>
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm text-white/70 mb-1 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Date
          </label>
          <input
            type="date"
            value={flightDate}
            onChange={(e) => setFlightDate(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm text-white/70 mb-1">
            Description (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="E.g., Business trip, Holiday, Family visit..."
            className="w-full min-h-[60px] text-sm md:text-base resize-none"
            maxLength={500}
          />
          <p className="text-xs text-white/50 mt-1">
            {notes.length}/500 characters
          </p>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="btn-primary w-full flex items-center justify-center gap-2 text-sm md:text-base"
        >
          <Plane className="w-4 h-4" />
          {isSubmitting ? 'Adding...' : 'Add Flight'}
        </button>
      </form>
    </div>
  );
}
